import { DocumentProcessingStatus, DocumentReviewState } from '../../interfaces/document';

export const DOCUMENT_TYPES = {
  REFERRAL_ORDER: 'referral-order',
  PRIOR_AUTHORIZATION: 'prior-authorization',
};

export const ACTIVE_DOCUMENT_TYPES = [
  DOCUMENT_TYPES.REFERRAL_ORDER,
  DOCUMENT_TYPES.PRIOR_AUTHORIZATION,
];

export type ActiveDocumentType = (typeof ACTIVE_DOCUMENT_TYPES)[number];

export const DOCUMENT_STATUS_COLORS = {
  // Success states
  completed: '#10b981',
  approved: '#10b981',
  active: '#10b981',
  synced: '#0891b2',

  // Error states
  failed: '#ef4444',
  denied: '#ef4444',
  rejected: '#ef4444',

  // Warning states
  pending: '#f59e0b',
  'in-progress': '#f59e0b',
  processing: '#f59e0b',
  // 'needs-review' deliberately absent: it is a review state, not a document status, and
  // its colour lives in REVIEW_STATE_PRESENTATION. Two maps owning one label is how the
  // same words ended up in two different reds in the same list.

  // Neutral states
  draft: '#6b7280',
  inactive: '#6b7280',
  'not-enabled': '#94a3b8',
  default: '#6b7280',
} as const;

export const isActiveDocumentType = (docType: string): docType is ActiveDocumentType => {
  return ACTIVE_DOCUMENT_TYPES.includes(docType as ActiveDocumentType);
};

export const getDocumentStatusColor = (status: string): string => {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '-');
  return (
    DOCUMENT_STATUS_COLORS[normalizedStatus as keyof typeof DOCUMENT_STATUS_COLORS] ||
    DOCUMENT_STATUS_COLORS.default
  );
};

export const formatStatusLabel = (status: string): string => {
  return status.replace(/[-_]/g, ' ').toLowerCase();
};

export const getDocumentTypeColors = (docType: string) => {
  if (docType === DOCUMENT_TYPES.REFERRAL_ORDER) {
    return {
      color: '#1e40af',
      background: '#dbeafe', 
      border: '#1e40af'
    };
  }
  if (docType === DOCUMENT_TYPES.PRIOR_AUTHORIZATION) {
    return {
      color: '#059669',
      background: '#ecfdf5',
      border: '#059669'
    };
  }
  return {
    color: '#4b5563',
    background: '#f8f9fa',
    border: '#6b7280'
  };
};

export const getStatusColor = (
  status: DocumentProcessingStatus | string,
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  if (status.includes(DocumentProcessingStatus.Completed)) {
    return 'success';
  } else if (status.includes(DocumentProcessingStatus.Pending)) {
    return 'warning';
  } else if (status.includes(DocumentProcessingStatus.Failed)) {
    return 'error';
  } else if (status.includes(DocumentProcessingStatus.Retry)) {
    return 'info';
  } else {
    return 'default';
  }
};

export const getStatusColorStyles = (status: DocumentProcessingStatus | string) => {
  if (status.includes(DocumentProcessingStatus.Completed)) {
    return {
      bgcolor: '#1eaf84',
      color: '#ffffff',
      borderColor: '#1eaf84',
    };
  } else if (status.includes(DocumentProcessingStatus.Pending)) {
    return {
      bgcolor: '#ff9800',
      color: '#ffffff',
      borderColor: '#ff9800',
    };
  } else if (status.includes(DocumentProcessingStatus.Failed)) {
    return {
      bgcolor: '#e74c3c',
      color: '#ffffff',
      borderColor: '#e74c3c',
    };
  } else if (status.includes(DocumentProcessingStatus.Retry)) {
    return {
      bgcolor: '#2196f3',
      color: '#ffffff',
      borderColor: '#2196f3',
    };
  } else {
    return {
      bgcolor: '#9e9e9e',
      color: '#ffffff',
      borderColor: '#9e9e9e',
    };
  }
};

export const getConfidenceColor = (confidence: number): 'success' | 'warning' | 'error' => {
  if (confidence >= 0.9) {
    return 'success';
  }
  if (confidence >= 0.7) {
    return 'warning';
  }
  return 'error';
};

export const getConfidenceColorStyles = (confidence: number) => {
  if (confidence >= 0.9) {
    return {
      bgcolor: '#379fba',
      color: '#ffffff',
      borderColor: '#379fba',
    };
  }
  if (confidence >= 0.7) {
    return {
      bgcolor: '#af77be',
      color: '#ffffff',
      borderColor: '#af77be',
    };
  }
  return {
    bgcolor: '#ff7b02',
    color: '#ffffff',
    borderColor: '#ff7b02',
  };
};

export const parseDocumentRules = (rules: any) => {
  if (!rules) {
    return null;
  }

  if (typeof rules === 'object') {
    return rules;
  }

  try {
    return JSON.parse(rules);
  } catch {
    return null;
  }
};

/**
 * Presentation for the server-derived review state.
 *
 * The labels are deliberately plain. The product brief asks that an operator read one of
 * three words rather than a pipeline status crossed with a pipeline step, because the
 * question being asked is "does this need me", not "where is this".
 *
 * `Processing` is a fourth state on purpose: a document that arrived minutes ago has not
 * parsed, does not need review and has not failed, and showing it as any of those would send
 * someone to work that does not exist. It resolves on its own — the stuck-document watchdog
 * escalates anything that stops moving into a terminal failure.
 */
export const REVIEW_STATE_PRESENTATION: Record<
  DocumentReviewState,
  { label: string; color: string; description: string }
> = {
  [DocumentReviewState.Parsed]: {
    label: 'parsed cleanly',
    color: '#16a34a',
    description: 'Data was extracted and nothing is flagged. Nothing to do.',
  },
  [DocumentReviewState.NeedsReview]: {
    label: 'needs review',
    color: '#d97706',
    description: 'Something was extracted, but it is flagged or incomplete.',
  },
  [DocumentReviewState.Failed]: {
    label: 'failed',
    color: '#dc2626',
    description: 'Nothing usable was extracted and the document is no longer moving.',
  },
  [DocumentReviewState.Processing]: {
    label: 'processing',
    color: '#6b7280',
    description: 'Still moving through the pipeline. Check back shortly.',
  },
};

/**
 * The states an operator has to act on — what the "needs attention" view shows.
 *
 * `failed` is included, not just `needs-review`: the brief asks that anything failed or
 * incomplete sit in *one* place to be worked, and a document that produced nothing is the
 * case that used to disappear entirely.
 */
export const REVIEW_STATES_NEEDING_ATTENTION: DocumentReviewState[] = [
  DocumentReviewState.NeedsReview,
  DocumentReviewState.Failed,
];

/**
 * True when a review filter is exactly the needs-attention set, so a quick-view button reflects
 * the view itself rather than merely whether some review filter happens to be set. Shared by
 * the Documents and Referrals lists so the two cannot drift apart.
 */
export const isNeedingAttentionSelection = (
  states: DocumentReviewState[] | undefined,
  attention: readonly DocumentReviewState[] = REVIEW_STATES_NEEDING_ATTENTION,
): boolean =>
  (states ?? []).length === attention.length &&
  attention.every((state) => states?.includes(state));

export const getReviewStatePresentation = (state?: DocumentReviewState | null) =>
  (state && REVIEW_STATE_PRESENTATION[state]) || {
    label: '-',
    color: '#6b7280',
    description: 'No review state reported.',
  };
